import { useEffect, useState, useRef } from 'react';
import { Grid2, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './Collection.css';
import Footer from '../components/Footer.jsx';
import { CODE_API_BASE_URL, aptos_network } from '../constants';
import { useAppContext } from '../AppContext';
import { useDevHub } from '../context/devhub/useDevHubSDK';
import { ROUTES } from '../routes/routeConstants';
import CloseIcon from '@mui/icons-material/Close';
// import LinkIcon from '@mui/icons-material/Link';

const nftImg = "https://d35wftsmcpsybh.cloudfront.net/_common/Epic_sample_medallion_2_3x.png";

// Social media sharing modal component
const ShareMedallionModal = ({ isOpen, onClose, medallion }) => {
  // Reference to the modal for animation timing
  const [mounted, setMounted] = useState(false);

  // Handle animation on mount/unmount
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;

  // Utility: Share to social media platforms
  const shareToSocial = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const message = `Universal Epic Universe Grand Opening Day Digital Medallion`; 
    const encodedMessage = encodeURIComponent(message);
    // Use the fallback image for preview
    const previewImage = encodeURIComponent(nftImg);
    
    window.nbc.identity.analyticsEventLogger.setMParticleUserAttribute("epiggo.social_share", platform);

    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodedMessage}&url=${url}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${encodedMessage}&picture=${previewImage}`, '_blank');
        break;
      default:
        break;
    }
  };
  
  // Handle close with animation
  const handleClose = () => {
    setMounted(false);
    setTimeout(() => {
      onClose();
    }, 300); // Match transition duration
  };

  return (
    <div style={{
      ...styles.overlay,
      opacity: mounted ? 1 : 0,
      visibility: mounted ? 'visible' : 'hidden',
    }}>      
      {/* Bottom sheet with sharing options */}
      <div style={{
        ...styles.shareSheet,
        marginTop: '60px !important'
        // transform: mounted ? 'translateY(0)' : 'translateY(100%)',
      }}>        
        <div style={styles.fullPreviewArea}>
          <div style={styles.spotifyPreviewCard}>
            <div style={styles.shareSheetHeader}>
              <button onClick={handleClose} style={styles.closeButtonModern}>
                <CloseIcon sx={{ color: '#ffffff', cursor: 'pointer' }}/>
              </button>
              {/* <div style={styles.shareTitle}>Share</div> */}
            </div>

            <img 
              src={medallion?.image || nftImg} 
              alt="Universal Epic Universe Grand Opening Day Digital Medallion" 
              style={styles.spotifyCardImage} 
            />
            <div style={styles.spotifyCardInfo}>
              <div style={{
                color: '#fff',
                fontSize: '18px',
                fontWeight: 700,
                marginBottom: '4px',
                letterSpacing: '2px'
              }}>UNIVERSAL EPIC UNIVERSE</div>
              <div style={styles.spotifyCardSubtitle}>Grand Opening Day Digital Medallion</div>
            </div>

            <div style={styles.shareButtons}>
              {/* <button
                aria-label="Copy Link"
                onClick={() => {
                  navigator.clipboard.writeText("https://epic-universe.backlot.club/");
                  alert('Link copied to clipboard!');
                }}
                style={styles.shareActionButton}
              >
                <div style={{...styles.socialIconContainerModern, backgroundColor: '#4CAF50'}}>
                  <div style={styles.iconWrapper}>
                    <LinkIcon fontSize="small" sx={{ color: '#ffffff', cursor: 'pointer' }} />
                  </div>
                </div>
                <span style={styles.socialLabelModern}>Copy Link</span>
              </button> */}
              
              <button
                aria-label="Share on X"
                onClick={() => shareToSocial('twitter')}
                style={styles.shareActionButton}
              >
                <div style={{...styles.socialIconContainerModern, backgroundColor: '#000000'}}>
                  <div style={styles.iconWrapper}>
                    𝕏
                  </div>
                </div>
                <span style={styles.socialLabelModern}>X</span>
              </button>
              
              <button
                aria-label="Share on Facebook"
                onClick={() => shareToSocial('facebook')}
                style={styles.shareActionButton}
              >
                <div style={{...styles.socialIconContainerModern, backgroundColor: '#1877F2'}}>
                  <div style={styles.iconWrapper}>
                    f
                  </div>
                </div>
                <span style={styles.socialLabelModern}>Facebook</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple Particle component for celebratory effects
const ParticleEffect = () => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Create particles only when component is mounted
    setMounted(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = window.innerWidth;
    const height = canvas.height = window.innerHeight;

    // Create particle system
    const particles = [];
    
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height / 2 + height / 4, // Start around the medallion
        size: Math.random() * 6 + 3,
        color: i % 3 === 0 ? '#d2ac55' : (i % 3 === 1 ? '#ffffff' : '#ffd700'),
        speedX: Math.random() * 4 - 2,
        speedY: Math.random() * -5 - 2, // Move up
        gravity: 0.05,
        opacity: 1,
        life: Math.random() * 80 + 60
      });
    }
    
    particlesRef.current = particles;
    
    // Animation loop
    let animationId;
    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      
      let hasActiveParticles = false;
      
      particlesRef.current.forEach(p => {
        if (p.life > 0) {
          hasActiveParticles = true;
          p.x += p.speedX;
          p.y += p.speedY;
          p.speedY += p.gravity;
          p.life--;
          p.opacity = p.life / 100;
          
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = p.color + Math.floor(p.opacity * 255).toString(16).padStart(2, '0');
          ctx.fill();
        }
      });
      
      if (hasActiveParticles) {
        animationId = requestAnimationFrame(animate);
      }
    };
    
    animate();
    
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [mounted]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 5
      }}
    />
  );
};

const Collection = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const navigate = useNavigate();

  // Display particle effects when medallion is shown
  const [showParticles, setShowParticles] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const { userProfile } = useAppContext();
  const { devHubUser } = useDevHub();
  const [isLoading, setIsLoading] = useState(false);  

  useEffect(() => {
    console.log('Collection', '1', userProfile.userId);
    setIsLoading(true);
    const getRedeemedCodesByUser = async () => {
      if (!userProfile.userId) {
        navigate(ROUTES.ROOT);
      }
      const url = `${CODE_API_BASE_URL}/codes/redeemedBy/${userProfile.userId}`;
      console.log("userId", userProfile.userId);
      const result = await fetch(url, {
        headers: {
          'accept': 'application/json',
          'Authorization': window.nbc.identity.token
        }
      });
      // Check if the user already has the medallion
      // If NOT, redirect to claim page
      let codes = await result.json();
      console.log("get redeemed codes >>>", codes);
      if (codes.length === 0) {
        navigate(ROUTES.CLAIM_CARD);
        return;
      }
      setIsLoading(false);
    };

    getRedeemedCodesByUser();

  }, [userProfile, navigate]);

  // Trigger particle effect once the medallion animations begin
  useEffect(() => {
    // Delay particle effects to coincide with medallion animation
    const timer = setTimeout(() => {
      setShowParticles(true);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div>
      {showParticles && <ParticleEffect />}
      <div className="background">
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            filter: 'drop-shadow(0 0 20px rgba(210, 172, 85, 0.5))',
            opacity: 1,
            scale: 1
          }}
        >
          <img 
            src={nftImg} 
            alt="Universal Epic Universe Grand Opening Day Medallion"
            style={{
              width: isSmallScreen ? '250px' : '300px',
              display: 'block',
              margin: '0 auto',
              marginTop: 40,
              // boxShadow: '0 0 10px rgba(210, 172, 85, 0.5)'
            }}
          />
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          visibility: showShareModal ? 'hidden' : 'visible',
          opacity: showShareModal ? 0 : 1,
        }}>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              delay: 1,
              duration: 0.8,
              ease: "easeOut"
            }}
            style={{
              color: '#d2ac55',
              textAlign: 'center',
              fontWeight: 300,
              fontSize: 28,
              // maxWidth: '90%',
              margin: '30px auto',
              textTransform: 'uppercase'
            }}
          >
            <motion.span
              animate={{
                // scale: [1, 1.2, 1],
                opacity: [1, 1, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatDelay: 5
              }}
              style={{ display: 'inline-block' }}
            >
              Congratulations!
            </motion.span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
          >
            <motion.h2 
              initial={{ x: 0, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ 
                delay: 1.5, 
                duration: 0.8,
                type: "spring",
                stiffness: 100 
              }}
              style={{
                color: '#fff',
                fontWeight: 600,
                textAlign: 'center',
                lineHeight: '48px',
                fontSize: 20,
                maxWidth: 780,
                display: 'block',
                margin: '0 auto',
                letterSpacing: 2
              }}
            >
              UNIVERSAL EPIC UNIVERSE
            </motion.h2>
            <motion.h2 
              initial={{ x: 0, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ 
                delay: 1.8, 
                duration: 0.8,
                type: "spring",
                stiffness: 100 
              }}
              style={{
                color: '#fff',
                fontWeight: 600,
                textAlign: 'center',
                lineHeight: '38px',
                fontSize: '31px',
                width: '80%',
                maxWidth: 780,
                display: 'block',
                margin: '0 auto',
                letterSpacing: 0
              }}
            >
              Grand Opening Day Digital Medallion
            </motion.h2>
          </motion.div>
          
          <Grid2 container sx={{
            fontSize: { xs: '0.9rem', sm: '1.2rem', md: '1.4rem', lg: '1.5rem', xl: '1.6rem' },
            maxWidth: { xs: '100%', sm: '30rem', md: '65%', lg: '45rem', xl: '100%' },
            lineHeight: { xs: '35px', md: '45px', xl: '64.704px' },
            margin: '0 auto'
          }}>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                delay: 2.2, 
                duration: 0.8 
              }}
              style={{
                textAlign: 'center',
                fontSize: isSmallScreen ? '14px' : '20px',
                lineHeight: isSmallScreen ? '20px' : '28px',
                maxWidth: 600,
                margin: isSmallScreen ? '20px auto' : '40px auto',
                padding: '0 10px'
              }}
            >
              For more fun, keep an eye out for Backlot.Club. Until then show  off your medallion on social media.
            </motion.p>
          </Grid2>
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              delay: 2.5, 
              duration: 0.8 
            }}
            whileHover={{ 
              scale: 1.05,
              boxShadow: '0 0 15px rgba(210, 172, 85, 0.7)'
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowShareModal(true)}
            style={{
              width: '100%',
              maxWidth: 300,
              display: 'block',
              border: '2px solid #f7d58e',
              backgroundColor: '#f7d58e',
              color: '#000000 !important',
              margin: '15px auto 15px auto',
              padding: '10px 20px',
              fontSize: '16px',
              cursor: 'pointer',
              borderRadius: 100
            }}
          >
            Share on Social Media
          </motion.button>          
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              delay: 2.7, 
              duration: 0.8 
            }}
            whileHover={{ 
              scale: 1.05,
              boxShadow: '0 0 15px rgba(210, 172, 85, 0.7)'
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(ROUTES.HOME)}
            className="secondary"
            style={{
              width: '100%',
              maxWidth: 300,
              display: 'block',
              border: '2px solid #f7d58e',
              backgroundColor: '#000000',
              color: '#ffffff !important',
              margin: '15px auto 30px auto',
              padding: '10px 20px',
              fontSize: '16px',
              cursor: 'pointer',
              borderRadius: 100
            }}
          >
            Back Home
          </motion.button>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              delay: 2.6, 
              duration: 0.8 
            }}
            whileHover={{ 
              scale: 1.05,
              boxShadow: '0 0 15px rgba(210, 172, 85, 0.7)'
            }}
            whileTap={{ scale: 0.95 }}
            onClick={
              () => {
                const explorerUrl = `https://explorer.aptoslabs.com/account/${devHubUser.address}/tokens?network=${aptos_network}`
                window.open(explorerUrl, '_blank');
              }
            }
            className="transparent"
            style={{
              width: '100%',
              maxWidth: 300,
              display: 'block',
              border: 'none !important',
              background: 'transparent !important',
              // color: '#000000 !important',
              margin: '-25px auto 15px auto',
              padding: '10px 20px',
              fontSize: '16px',
              cursor: 'pointer',
              borderRadius: 100
            }}
          >
            View on Aptos
          </motion.button>

          </div>
      </div>
        <Footer />
        
        {/* Share Medallion Modal */}
        <ShareMedallionModal 
          isOpen={showShareModal} 
          onClose={() => setShowShareModal(false)} 
          medallion={{ name: "Universal Epic Universe Grand Opening Day Digital Medallion" }} 
        />
    </div>
  );
};

// Basic styles for modal and overlay
const styles = {
  overlaySm: {
    position: 'fixed',
    overflow: 'auto',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0)', // Dim the entire background
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'top',
    zIndex: 1000, // Ensure the modal is on top
  },
  modal: {
    backgroundColor: '#000',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    // flexDirection: 'row',
    flexDirection: window.innerWidth <= 768 ? 'column' : 'row', // Stack vertically on small screens
    justifyContent: 'space-between',
    alignItems: 'center',
    marginLeft: '10rem',
  },
  closeButton: {
    position: 'fixed',
    top: '-30px',
    right: '10px',
    // padding: '23px',
    backgroundColor: 'black',
    border: '2px solid #008bd4',
    opacity: 0.7,
    borderRadius: '300px',
    width: '26px',
    height: '26px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 0
  },
  card: {
    marginBottom: 30,

  },
  count: {
    color: '#ffffff',
    textAlign: 'right'
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)', // Semi-transparent black background
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 1000,
    transition: 'opacity 0.3s ease-out, visibility 0.3s ease-out',
  },
  // Full screen preview area (Spotify-like)
  fullPreviewArea: {
    width: '100%',
    // padding: '20px',
  },
  spotifyPreviewCard: {
    backgroundColor: '#292929',
    borderRadius: '12px',
    padding: '10px 50px 40px 50px',
    maxWidth: '300px',
    // width: '90%',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
    // display: 'flex',
    // flexDirection: 'column',
    display: 'block',
    margin: '0 auto'
  },
  spotifyCardImage: {
    width: '100%',
    aspectRatio: '1/1',
    borderRadius: '8px',
    objectFit: 'cover',
    marginBottom: '12px',
  },
  spotifyCardInfo: {
    padding: '4px',
    textAlign: 'center'
  },
  spotifyCardSubtitle: {
    color: '#ffffff',
    fontSize: '28px',
  },
  // Modern share sheet styles
  shareSheet: {
    // backgroundColor: '#1f1f1f',
    width: '100%',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
    transition: 'transform 0.3s ease-out',
    boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.3)',
    maxWidth: 500,
    margin: '60px auto 0 auto'
  },
  shareSheetHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px 8px 20px',
    position: 'relative',
  },
  shareTitle: {
    color: '#fff',
    textAlign: 'center',
    fontSize: '18px',
    fontWeight: 600,
    width: '100%',
  },
  closeButtonModern: {
    position: 'absolute', 
    top: '-20px',
    right: '-30px',
    background: 'none',
    border: 'none',
    color: '#fff',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '8px',
    opacity: 0.8,
    zIndex: 2,
  },
  shareButtons: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: '10px 20px 30px 20px',
    // width: '100%',
  },
  shareActionButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '10px',
    color: '#fff',
    width: '80px',
  },
  socialIconContainerModern: {
    width: '45px',
    height: '45px',
    borderRadius: '50%', 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    color: '#fff',
  },
  iconWrapper: {
    fontSize: '24px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '60px',
    height: '60px',
    color: 'white',
  },
  socialLabelModern: {
    color: '#fff',
    fontSize: '12px',
    marginTop: '4px',
    textAlign: 'center',
  }
}

export default Collection;